import { useEffect, useRef, useState, type ReactNode } from 'react';

type RevealOnScrollProps = {
  children: ReactNode;
  className?: string;
  /**
   * `'fade'` (default) fades/slides content in, matching the mockup's
   * `.rise`/`.rise.in` behaviour. `'wipe'` plays a gentle squeegee sweep
   * instead — a `clip-path` wipe left-to-right (see the reference identity
   * study's squeegee reveal) — and should be used sparingly, on a few key
   * sections only. Both variants share the same observer/reduced-motion
   * handling below; only the CSS differs.
   */
  variant?: 'fade' | 'wipe';
};

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Wraps children and reveals them once they enter the viewport. Renders
 * fully visible immediately (no observer, no clip/transform) under
 * `prefers-reduced-motion: reduce` for both variants.
 *
 * The observed node and the animated node are deliberately two different
 * elements: the outer div (holding the `ref`) never carries `clip-path` or
 * `opacity`, while an inner div carries the `'wipe'` variant's clip-path.
 * Chromium treats a `clip-path`-clipped-to-zero-width box as having zero
 * intersection area — observing that same element directly would mean
 * `isIntersecting` never goes true once scrolled into view, permanently
 * stuck hidden. Keeping the observed box unclipped avoids that trap.
 */
export const RevealOnScroll = ({ children, className, variant = 'fade' }: RevealOnScrollProps) => {
  const ref = useRef<HTMLDivElement>(null);
  // Lazy initializer reads the media query once, up front, so reduced-motion
  // never needs a setState call inside the effect below.
  const [isRevealed, setIsRevealed] = useState(prefersReducedMotion);

  useEffect(() => {
    const node = ref.current;
    if (!node || prefersReducedMotion()) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsRevealed(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const motionClassName =
    variant === 'wipe'
      ? `reveal-wipe ${isRevealed ? 'is-revealed' : ''}`
      : `transition-[opacity,transform] duration-[600ms] ease-out ${
          isRevealed ? 'translate-y-0 opacity-100' : 'translate-y-[14px] opacity-0'
        }`;

  return (
    <div ref={ref} className={className}>
      <div className={motionClassName}>{children}</div>
    </div>
  );
};
